import type { MasteryMapDocument, MasteryMapListItem } from '../model/types/mastery-map/interfaces'
import type { MasteryDashboardData } from '../model/types/mastery-map-dashboard/interfaces'

import { AssessmentType } from '@brighterly/lib-core-types/student-assessment/enums'

import { MasteryLevel } from '../model/types/mastery-map/enums'

const SKILLS: [code: string, label: string][] = [
  ['6.RP.1', 'Describe a ratio relationship between two quantities'],
  ['6.RP.2', 'Understand a unit rate'],
  ['6.RP.3a', 'Make tables of equivalent ratios'],
  ['6.RP.3d', 'Convert units of measure using ratios'],
  ['6.NS.1', 'Divide fractions by fractions'],
  ['6.NS.2', 'Divide multi-digit numbers'],
  ['6.NS.5', 'Use positive and negative numbers in context'],
  ['6.NS.7c', 'Understand absolute value as distance from zero'],
  ['6.EE.1', 'Write and evaluate expressions with exponents'],
  ['6.EE.2', 'Write, read and evaluate expressions with letters'],
  ['6.EE.7', 'Write and solve equations like x + p = q'],
  ['6.EE.8', 'Write an inequality to describe a limit, like x > 3'],
  ['6.G.1', 'Find the area of triangles and polygons'],
  ['6.G.3', 'Draw shapes on a coordinate grid from their corners'],
  ['6.G.4', 'Find surface area by unfolding a 3D shape into a net'],
  ['6.SP.1', 'Recognise a statistical question'],
  ['6.SP.4', 'Show data on dot plots, histograms and box plots'],
  ['6.SP.5a', 'Say how many values were collected'],
]

const { Mastered: M, Developing: D, NeedsSupport: N } = MasteryLevel
const U = null

const LEVELS_BY_TEST: (MasteryLevel | null)[][] = [
  [D, N, M, U, M, D, N, U, D, N, U, U, M, D, U, N, U, U],
  [M, D, M, D, M, M, D, U, M, D, D, U, M, D, U, D, N, U],
  [M, M, M, D, M, M, M, D, M, M, D, D, M, M, D, D, D, N],
  [M, M, M, M, M, M, M, D, M, M, M, D, M, M, D, M, D, D],
]

const DOMAINS = [
  { code: '6.RP', label: 'Ratios & Proportional Relationships', percentage: 0 },
  { code: '6.NS', label: 'The Number System', percentage: 0 },
  { code: '6.EE', label: 'Expressions & Equations', percentage: 0 },
  { code: '6.G', label: 'Geometry', percentage: 0 },
  { code: '6.SP', label: 'Statistics & Probability', percentage: 0 },
]

const BASE_ID = 900
const GENERATED_DATES = ['2026-08-04', '2026-08-25', '2026-09-15', '2026-10-06']

const toDocument = (levels: (MasteryLevel | null)[], index: number): MasteryMapDocument => ({
  assessment_id: BASE_ID + index,
  schema: 1,
  grade_label: 'Grade 6',
  generated_at: `${GENERATED_DATES[index]}T10:00:00+00:00`,
  student_name: 'Alex',
  subject_label: 'Math',
  standards: SKILLS.flatMap(([code, label], skillIndex) => {
    const level = levels[skillIndex]

    return level ? [{ code, label, level, earned: 1, max: 2, percentage: 50 }] : []
  }),
  not_assessed: SKILLS.filter((_, skillIndex) => levels[skillIndex] === null).map(([code, label]) => ({ code, label })),
  domains: DOMAINS,
  strongest_domain: null,
  focus_domain: null,
  stats: [],
  prose: { title: '', intro: '', meaning: '', overview: '', focusIntro: '' },
})

const NON_BASELINE_TYPE = 'UNIT' as AssessmentType

/** Dev preview only: a Baseline and three later tests, until the backend serves tests after Baseline (RTNT-590). */
export const getMasteryDashboardPreview = (testCount: number): MasteryDashboardData => {
  const levels = LEVELS_BY_TEST.slice(0, Math.max(1, Math.min(testCount, LEVELS_BY_TEST.length)))
  const documents = levels.map(toDocument)

  const list: MasteryMapListItem[] = documents.map((document, index) => ({
    assessment_id: document.assessment_id,
    type: index === 0 ? AssessmentType.Baseline : NON_BASELINE_TYPE,
    type_label: index === 0 ? 'Baseline' : 'Unit test',
    title: 'Grade 6 Math',
    generated_at: document.generated_at,
    is_viewed: index < documents.length - 1,
  })).reverse()

  return {
    list,
    documents: Object.fromEntries(documents.map(document => [document.assessment_id, document])),
  }
}
